import React, { useCallback, useEffect } from 'react';
import {
  RouterProvider,
  createBrowserRouter,
  Navigate,
} from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import theme from '@keyslabor/shared/theme';

import { Routes } from '@/globalTypes';
import AppGlobalStyle from '@keyslabor/shared/containers/AppGlobalStyle';
import LostHours from '@/containers/LostHours';
import useBreakpoint, {
  mobileWidth,
} from '@keyslabor/shared/hooks/useBreakPoint';
import TurnOver from '@/containers/TurnOver';
import { Toaster } from '@/components/ui/sonner';
import { PageLoader } from '@/components/PageLoader';
import * as Sentry from '@sentry/react';
import { ModalManager } from '@/components/modal-manager.tsx';

const HoursByDay = React.lazy(() => import('../HoursByDay'));
const EmployeeList = React.lazy(() => import('../Employee'));
const RequestEmployeeList = React.lazy(() => import('../RequestEmployeeList'));
const ManagerProperty = React.lazy(() => import('../ManagerProperty'));
const ChangePassword = React.lazy(() => import('../ChangePassword'));
const ManagerLogin = React.lazy(() => import('../ManagerLogin'));
const ManagerLogout = React.lazy(() => import('../ManagerLogout'));
const Schedule = React.lazy(() => import('../Schedule/Schedule'));
const InvoiceList = React.lazy(() => import('../InvoiceList'));
const InvoiceCreate = React.lazy(() => import('../InvoiceCreate'));
const Billing = React.lazy(() => import('../Billing'));
const Leads = React.lazy(() => import('../Leads'));
const OpenPositions = React.lazy(() => import('../Leads/OpenPositions'));
const ShiftDay = React.lazy(() => import('../ShiftDay'));
const ReviewReport = React.lazy(() => import('../ReviewReport'));
const AppEmpty = React.lazy(() => import('./AppNotFound'));
const Stories = React.lazy(() => import('../../stories/Stories'));

const router = createBrowserRouter([
  {
    element: <ModalManager />,
    children: [
      {
        path: '/',
        element: <Navigate to={Routes.ManagerRoot} />,
      },
      {
        path: Routes.ManagerRoot,
        element: <Navigate to={Routes.EmployeeList} />,
      },
      {
        path: '/stories',
        element: <Stories />,
      },
      {
        path: Routes.Login,
        element: <ManagerLogin />,
      },
      {
        path: Routes.LogOut,
        element: <ManagerLogout />,
      },
      {
        path: Routes.PropertySelection,
        element: <ManagerProperty />,
      },
      {
        path: Routes.PasswordChange,
        element: <ChangePassword />,
      },
      {
        path: Routes.EmployeeList,
        element: <EmployeeList />,
      },
      {
        path: Routes.RequestEmployeeList,
        element: <RequestEmployeeList />,
      },
      {
        path: Routes.TrackingByDay,
        element: <HoursByDay />,
      },
      {
        path: Routes.InvoiceList,
        element: <InvoiceList />,
      },
      {
        path: Routes.Billing,
        element: <Billing />,
      },
      {
        path: Routes.ScheduleList,
        element: <Schedule />,
      },
      {
        path: Routes.InvoiceCreate,
        element: <InvoiceCreate />,
      },
      {
        path: Routes.LostHoursList,
        element: <LostHours />,
      },
      {
        path: Routes.TurnOver,
        element: <TurnOver />,
      },
      {
        path: Routes.LeadsInhouse,
        element: <Leads filter={'inhouse'} />,
      },
      {
        path: Routes.LeadsByAgency,
        element: <Leads filter={'by-agency'} />,
      },
      {
        path: Routes.OpenPositions,
        element: <OpenPositions />,
      },
      {
        path: Routes.ShiftDay,
        element: <ShiftDay />,
      },
      {
        path: Routes.ReviewReport,
        element: <ReviewReport />,
      },
      {
        path: '*',
        element: <AppEmpty />,
      },
    ],
  },
]);

const queryClient = new QueryClient();

function App() {
  const isMobile = useBreakpoint(mobileWidth);

  const handleViewportSize = useCallback(() => {
    const siteWidth = isMobile ? mobileWidth : window.screen.width;
    const scale = window.screen.width / siteWidth;
    const viewPortEl = document.querySelector('meta[name="viewport"]');
    if (viewPortEl) {
      viewPortEl.setAttribute(
        'content',
        'width=' + siteWidth + ', initial-scale=' + scale + ''
      );
    }
  }, [isMobile]);

  useEffect(() => {
    handleViewportSize();
    window.addEventListener('resize', handleViewportSize);
    return () => {
      window.removeEventListener('resize', handleViewportSize);
    };
  }, [handleViewportSize]);

  return (
    <>
      <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
        <AppGlobalStyle theme={theme} />
        <QueryClientProvider client={queryClient}>
          <React.Suspense fallback={<PageLoader />}>
            <RouterProvider router={router} />
          </React.Suspense>
        </QueryClientProvider>
        <Toaster />
      </Sentry.ErrorBoundary>
    </>
  );
}

export default App;
